import { TMasterLanguageTestLevel } from "@/api/entities/master-language-test.entity";
import { updateLanguageTestApi } from "@/api/services/global/master";
import { TGetLanguageTestListOutputDto } from "@/api/services/global/master/dtos/languageTest/get-master-language-test-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Textarea } from "@/components/elements/Textarea";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { UpdateLanguageTestFormTable } from "@/features/master-data/components/LanguageTest/UpdateLanguageTestFormTable";
import { languageTestSchema } from "@/features/master-data/schema/masterDataLanguageTestSchema";
import { TLanguageTestForm } from "@/features/master-data/types/masterDataLanguageTestForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormLabel, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";

type TUpdateLanguageTestFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedLanguageTest?: TGetLanguageTestListOutputDto;
};

export const UpdateLanguageTestFormModal: FC<TUpdateLanguageTestFormModalProps> = ({ onClose, isOpen, selectedLanguageTest }) => {
  const queryClient = useQueryClient();

  const [selectedLevelRows, setSelectedLevelRows] = useState<TMasterLanguageTestLevel[]>(selectedLanguageTest?.levels ?? []);

  const methods = useForm<TLanguageTestForm>({
    resolver: yupResolver(languageTestSchema),
    mode: "onBlur",
    defaultValues: {
      testName: selectedLanguageTest?.testName,
      description: selectedLanguageTest?.description,
      levels: selectedLanguageTest?.levels ?? [],
    },
  });

  const { control } = methods;

  const onSubmit = async (data: TLanguageTestForm) => {
    try {
      const rs = await updateLanguageTestApi({
        languageTests: [
          {
            id: toNumber(selectedLanguageTest?.id),
            testName: data.testName,
            description: data.description,
            levels: selectedLevelRows.map((item) => ({
              id: toNumber(item.id),
              level: item.level,
            })),
          },
        ],
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_LANGUAGE_TEST] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal width={1200} title="編集" isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={languageTestSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField control={control} name="testName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="description">
              <Textarea />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <Stack gap={2}>
              <FormField>
                <FormLabel required>レベル</FormLabel>
              </FormField>
              <UpdateLanguageTestFormTable
                selectedLevelRows={selectedLevelRows}
                setSelectedLevelRows={setSelectedLevelRows}
                existingLanguageTestId={toNumber(selectedLanguageTest?.id)}
                existingLanguageTestName={selectedLanguageTest?.testName ?? EMPTY_STR.TEXT}
                existingDescription={selectedLanguageTest?.description ?? EMPTY_STR.TEXT}
              />
            </Stack>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
